import {
  CouriersControllerListOptionalParams,
  KnownSortOrder,
  KnownUserStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';

/** The list query as the controls express it; `pageNo` belongs to the infinite query, not the caller. */
export type CouriersListQueryParams = Omit<CouriersControllerListOptionalParams, 'pageNo'> & {
  sortOrder?: KnownSortOrder;
};

/**
 * The controls the list header owns. `groupID` is the carrier filter.
 *
 * `status` is one value, not the endpoint's array: the select offers one and the URL carries one.
 */
export type CouriersListControlsState = Required<
  Pick<CouriersListQueryParams, 'sortBy' | 'sortOrder' | 'searchTerm' | 'groupID'>
> & { status: string };

export const COURIERS_PAGE_SIZE = 25;

/** What the status select emits for "don't filter". Not a status the API knows. */
export const ALL_STATUSES = 'all';

/** What callers pass: the query params, with `status` as one value or several. */
export type CouriersListQueryInput = Omit<Partial<CouriersListQueryParams>, 'status'> & {
  status?: string | string[];
};

/** One value or several, with the header's 'all' dropped — it is not a status the API knows. */
function toStatuses(status: string | string[] | undefined): string[] | undefined {
  const statuses = (Array.isArray(status) ? status : [status]).filter(
    (value): value is string => !!value && value !== ALL_STATUSES
  );

  return statuses.length > 0 ? statuses : undefined;
}

/** The controls as the endpoint wants them: blanks dropped, derived params settled. */
export function makeCouriersListQueryParams(params: CouriersListQueryInput): CouriersListQueryParams {
  const statuses = toStatuses(params.status);

  return {
    status: statuses,
    sortBy: params.sortBy || undefined,
    sortOrder: params.sortOrder,
    searchTerm: params.searchTerm || undefined,
    groupID: params.groupID || undefined,
    updatedFrom: params.updatedFrom,
    // A carrier's couriers sit in its regions too, so the filter has to reach beneath it.
    includeChildGroups: params.groupID ? true : undefined,
    // Removed couriers are hidden unless asked for, so a bare 'deleted' would match nothing.
    includeRemoved: statuses?.includes(KnownUserStatus.Deleted) || undefined,
    // Likewise for 'incomplete'.
    includeIncomplete: statuses?.includes(KnownUserStatus.Incomplete) || undefined
  };
}
